/**
 * Braintree card fields (Hosted Fields) - the replacement for the retired
 * Drop-in UI, built the same way as the customer portal.
 *
 * PCI: the card number, expiry, CVV (and ZIP / name where asked for) are typed
 * into iframes Braintree serves; this app only ever sees the one-time nonce.
 */
import client from 'braintree-web/client'
import hostedFields from 'braintree-web/hosted-fields'

const clients = new Map()

/** one Braintree client per token */
function clientFor(token) {
  if (!clients.has(token))
    clients.set(token, client.create({authorization: token}).catch((e) => {
      clients.delete(token)
      throw e
    }))
  return clients.get(token)
}

/** inside the iframes only system fonts are allowed */
const FIELD_STYLES = {
  input: {'font-size': '15px', 'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#212529'},
  ':focus': {color: '#111'},
  '.invalid': {color: '#c62828'},
  '::placeholder': {color: '#9e9e9e'},
}

/**
 * Mount the card fields into the given elements.
 * @param {string} token client token from /api/transaction/token or the vault
 * @param {{number: HTMLElement, expiry: HTMLElement, cvv: HTMLElement, zip?: HTMLElement, name?: HTMLElement}} el
 * @param {{zip?: string}} opts prefill for the ZIP field
 */
export async function mountCardFields(token, el, opts = {}) {
  const fields = {
    number: {container: el.number, placeholder: '1234 1234 1234 1234'},
    expirationDate: {container: el.expiry, placeholder: 'MM / YY'},
    cvv: {container: el.cvv, placeholder: 'CVV'},
  }
  if (el.zip)
    fields.postalCode = {container: el.zip, placeholder: 'ZIP', prefill: opts.zip || undefined}
  if (el.name)
    fields.cardholderName = {container: el.name, placeholder: 'Name on card'}

  return hostedFields.create({client: await clientFor(token), styles: FIELD_STYLES, fields})
}

/** what to fix, in words, from a tokenize failure */
export function cardFieldProblem(e) {
  const names = {number: 'card number', expirationDate: 'expiry', cvv: 'CVV', postalCode: 'ZIP', cardholderName: 'name on card'}
  if (e && e.code === 'HOSTED_FIELDS_FIELDS_EMPTY')
    return 'Enter the card details.'
  if (e && e.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
    const bad = ((e.details && e.details.invalidFieldKeys) || []).map((k) => names[k] || k)
    return bad.length ? `Check the ${bad.join(', ')}.` : 'Check the card details.'
  }
  if (e && e.code === 'HOSTED_FIELDS_FAILED_TOKENIZATION')
    return 'The card could not be read. Check the details, or try another card.'
  return (e && e.message) || 'The card could not be read. Try again.'
}
